import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Menu } from "lucide-react";

const NavLink = ({ to, children }) => {
  const loc = useLocation();
  const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
  return (
    <Link
      to={to}
      className={cn(
        "px-3 py-2 rounded-md text-sm font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )}
    >
      {children}
    </Link>
  );
};

export default function AdminHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { to: "/admin", label: "Översikt" },
    { to: "/admin/ads", label: "Annonser" },
    { to: "/admin/lineups", label: "Laguppställningar" },
    ...(user?.role === "super_admin"
      ? [
          { to: "/admin/guest-riders", label: "Inlånade förare" },
          { to: "/admin/notifications", label: "Notiser" },
          { to: "/admin/match-structure", label: "Matchstruktur" },
          { to: "/admin/app-releases", label: "Appuppdateringar" },
        ]
      : []),
  ];

  const onLogout = () => {
    logout();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="w-full border-b bg-background/60 backdrop-blur">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="font-semibold text-lg">Admin</Link>
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to}>{item.label}</NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="md:hidden">
                <Menu className="h-4 w-4" />
                <span className="sr-only">Öppna adminmeny</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 border bg-background shadow-md">
              {navItems.map((item) => {
                const active =
                  location.pathname === item.to ||
                  location.pathname.startsWith(item.to + "/");
                return (
                  <DropdownMenuItem
                    key={item.to}
                    asChild
                    className={cn(
                      "cursor-pointer",
                      active && "bg-muted font-medium text-foreground"
                    )}
                  >
                    <Link to={item.to}>{item.label}</Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          {user?.email && (
            <span className="hidden text-sm text-muted-foreground sm:inline">
              {user.email} ({user.role})
            </span>
          )}
          <Button asChild variant="outline" size="sm">
            <Link to="/">Till appen</Link>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm">Logga ut</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Logga ut?</AlertDialogTitle>
                <AlertDialogDescription>
                  Du loggas ut från admin och skickas tillbaka till inloggningen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction onClick={onLogout}>Logga ut</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <Separator />
    </div>
  );
}
